import { BEIGE_600, GRAY_700, GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { FC, useCallback, useMemo } from 'react'
import { Stack, Typography } from '@mui/material'
import { WorkspaceMemberProfile, WorkspaceRole, WorkspaceSeatType } from 'models/user/userWorkspaceDTOs'

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUITooltip } from 'components/common/MUITooltip'
import { useTranslation } from 'react-i18next'

export enum SeatType {
  FULL_ACCESS = 'full_access',
  RESTRICTED_ACCESS = 'restricted_access',
  SPONSORED_ACCESS = 'sponsored_access',
}

export interface SeatTypeItem {
  type: SeatType
  seatType: WorkspaceSeatType
  title: string
  subTitle: string
}

// Order kept in sync with getAllowedSeatTypes / accessDropdownItems so every seat-type surface
// presents the options in the same order (ISSUE-2078): Full, Sponsored, Restricted.
const seatTypeItems: SeatTypeItem[] = [
  { type: SeatType.FULL_ACCESS, seatType: WorkspaceSeatType.FULL, title: SeatType.FULL_ACCESS, subTitle: 'full_access_description' },
  { type: SeatType.SPONSORED_ACCESS, seatType: WorkspaceSeatType.SPONSORED, title: SeatType.SPONSORED_ACCESS, subTitle: 'sponsored_access_description' },
  { type: SeatType.RESTRICTED_ACCESS, seatType: WorkspaceSeatType.RESTRICTED, title: SeatType.RESTRICTED_ACCESS, subTitle: 'restricted_access_description' },
]

/**
 * Seat-type options to show for a member (ISSUE-2078). Offers the plan-allowed seat types, but always
 * keeps the member's current seat type in the list so it still renders as the selected row even when
 * the plan no longer permits it (#7). Exported for unit testing.
 */
export const getSeatTypeDropdownItems = (allowedSeatTypes: WorkspaceSeatType[], memberSeatType: WorkspaceSeatType | undefined): SeatTypeItem[] =>
  seatTypeItems.filter((item) => allowedSeatTypes.includes(item.seatType) || item.seatType === memberSeatType)

/**
 * Whether a seat-type option must be disabled for the given member. Admin rows only:
 * - Paid plan (Pro / Enterprise): an admin always holds a Full seat, so every other option is disabled
 *   — paid workspaces must not contain restricted or sponsored admins (ISSUE-2613).
 * - Free plan: an admin's seat can be Full or Restricted but never Sponsored (ISSUE-2488).
 * Disabled options stay visible with a tooltip rather than disappearing. Exported for unit testing.
 */
export const isSeatTypeOptionDisabledForRole = (memberRole: WorkspaceRole | undefined, itemSeatType: WorkspaceSeatType, isPaidPlan = false): boolean => {
  if (memberRole !== WorkspaceRole.ADMIN) return false
  return isPaidPlan ? itemSeatType !== WorkspaceSeatType.FULL : itemSeatType === WorkspaceSeatType.SPONSORED
}

/** Maps the backend seat type onto the UI option. Each backend value has a distinct UI seat type. */
export const getMemberSeatType = (member: WorkspaceMemberProfile): SeatType => {
  switch (member.seatType) {
    case WorkspaceSeatType.SPONSORED:
      return SeatType.SPONSORED_ACCESS
    case WorkspaceSeatType.RESTRICTED:
      return SeatType.RESTRICTED_ACCESS
    default:
      return SeatType.FULL_ACCESS
  }
}

interface Props {
  member: WorkspaceMemberProfile
  /** Seat types the workspace plan allows assigning (ISSUE-2078). Only these are offered. */
  allowedSeatTypes: WorkspaceSeatType[]
  /** Whether the workspace is on a paid plan — admins must hold a Full seat there (ISSUE-2613). */
  isPaidPlan?: boolean
  disabled?: boolean
  openDropdownId: string | null
  onOpen: (id: string) => void
  onClose: () => void
  onChangeSeatType?: (member: WorkspaceMemberProfile, item: SeatTypeItem) => void
}

export const SeatTypeDropdown: FC<Props> = ({ member, allowedSeatTypes, isPaidPlan = false, disabled = false, openDropdownId, onOpen, onClose, onChangeSeatType }) => {
  const { t } = useTranslation(['profile', 'workspace_profile'])
  const dropdownId = `seat-type-${member.membershipId}`
  const isOpen = openDropdownId === dropdownId

  const items = useMemo(() => getSeatTypeDropdownItems(allowedSeatTypes, member.seatType), [allowedSeatTypes, member.seatType])

  const getSeatTypeLabel = useCallback((): string => {
    switch (member.seatType) {
      case WorkspaceSeatType.SPONSORED:
        return t('profile:members_content.sponsored')
      case WorkspaceSeatType.RESTRICTED:
        return t('profile:members_content.restricted')
      default:
        return t('workspace_profile:full_access')
    }
  }, [member, t])

  const getSeatTypeColor = useCallback((): string => (member.seatType === WorkspaceSeatType.FULL ? GRAY_900 : GRAY_700), [member])

  return (
    <MUIDropdown
      open={isOpen}
      onClose={onClose}
      sx={{ width: '36rem', zIndex: 11 }}
      button={() => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: '100%',
            borderRadius: '0.8rem',
            padding: '0 1.2rem',
            height: '4.2rem',
            cursor: disabled ? 'default' : 'pointer',
            ':hover': {
              backgroundColor: disabled ? undefined : BEIGE_600,
            },
          }}
          onClick={(e) => {
            e.stopPropagation()
            if (isOpen) return onClose()
            if (!disabled) return onOpen(dropdownId)
          }}
        >
          <Stack direction="row" alignItems="center">
            <Typography variant="text-sm" fontWeight={350} color={getSeatTypeColor()}>
              {getSeatTypeLabel()}
            </Typography>
          </Stack>
          {!disabled && (isOpen ? <KeyboardArrowUpRoundedIcon sx={{ color: GRAY_800, fontSize: '2.4rem' }} /> : <KeyboardArrowDownRoundedIcon sx={{ color: GRAY_800, fontSize: '2.4rem' }} />)}
        </Stack>
      )}
    >
      {items.map((item) => {
        // On a paid plan an admin must hold a Full seat (ISSUE-2613); on a free plan they may be Full or
        // Restricted but never Sponsored (ISSUE-2488). Disallowed options stay visible but disabled,
        // with a tooltip explaining why they can't be picked for an admin.
        const isDisabledForAdmin = isSeatTypeOptionDisabledForRole(member.role, item.seatType, isPaidPlan)

        const dropdownItem = (
          <MUIDropdownItem key={item.type} disabled={isDisabledForAdmin} isSelected={getMemberSeatType(member) === item.type} onClick={() => onChangeSeatType?.(member, item)}>
            <Stack gap={0.2} sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                  {t(`workspace_profile:${item.title}`)}
                </Typography>
                {item.type === SeatType.FULL_ACCESS && <MUIBadge label={t('profile:members_content.badge_paid')} color="blue" size="sm" />}
                {item.type === SeatType.RESTRICTED_ACCESS && <MUIBadge label={t('profile:members_content.badge_free')} color="gray" size="sm" />}
                {item.type === SeatType.SPONSORED_ACCESS && (
                  <>
                    <MUIBadge label={t('profile:members_content.badge_paid')} color="blue" size="sm" />
                    <MUIBadge label={t('profile:members_content.badge_enterprise_only')} color="green" size="sm" />
                  </>
                )}
              </Stack>
              <Typography variant="text-sm" color={GRAY_700} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {t(`workspace_profile:${item.subTitle}`)}
              </Typography>
            </Stack>
          </MUIDropdownItem>
        )

        return isDisabledForAdmin
          ? <MUITooltip key={item.type} content={isPaidPlan ? t('profile:members_content.admin_full_seat_only_tooltip') : t('profile:members_content.admin_no_sponsored_tooltip')} placement="left">{dropdownItem}</MUITooltip>
          : dropdownItem
      })}
    </MUIDropdown>
  )
}
